import type { FileUploadSession } from "@/db/fileUploadDb";

/**
 * LSP-19609: the lane's half of "a row that enters 'Finishing in the background' also leaves it".
 *
 * When the foreground lane yields a row — its durable writer claim was refused because a drain in
 * another tab took the row, or a transient failure handed the row to the durable retry ladder — the
 * dialog used to treat that as terminal for the lane: set `entry.deferred`, render "Finishing in the
 * background…", and never look at the row again. Whatever happened next was invisible to the tab
 * that holds the File and the user's attention:
 *
 *  - the other side finished the row, and the tray still said "finishing" (0 "Row left" logs);
 *  - the other side gave up (POST /complete timed out under load, or a pre-LSP-19537 build's drain
 *    parked it) and the row came back as ownerless `pending` — a row THIS lane could upload in one
 *    attempt, but nothing in this tab was ever going to try again;
 *  - this tab's own drain could not step in either, because the scheduler lease was held by the
 *    same foreign tab for the life of its page.
 *
 * So the dialog keeps ONE bounded watch over every row it has deferred and mirrors the truth into
 * the tray: a completed row is marked complete, a cancelled row cancelled, and an ownerless pending
 * row that is due again is re-attempted by the lane itself through the ordinary upload path (the
 * caller decides how — same slot, same retry ladder, same fenced claim). A row still owned elsewhere
 * is left alone; past its deadline the watch gives up and says so, and the row remains exactly where
 * the durable drains already expect to find it.
 *
 * Shape decisions, each from the plan review:
 *  - One registry, one timer, one bulk read per tick — not a poller per row. A 932-file batch that
 *    deferred every row would otherwise issue ~60 reads per row per lifetime.
 *  - The deadline is the CALLER's, stored on the entry, so a row that yields, is re-attempted, and
 *    yields again does not restart its clock. Re-attempts are budgeted by the caller the same way.
 *  - A missing row is NOT success. Admin's completion path deletes the row and its chunks in the same
 *    fenced write that marks it complete, and the bytes-gone discard deletes it too, so "missing" is
 *    only resolved by the caller's authoritative `onRowMissing` probe (the upload registry).
 *  - Whether a re-attempt is allowed at all is the caller's `canReattempt(row)`: the lost-target
 *    restart hands the row to persisted resume precisely because its own target is dead, and a row
 *    whose durable upload identity has moved on is not the row this entry holds a File for.
 *
 * Deliberately pure: every side effect is injected, so the loop is unit-tested with fake timers and
 * the dialog wiring stays small. No status is written here — `onReattempt` writes only through the
 * driver's fenced claim, and `onComplete`/`onCancelled` only mirror what the row (or registry) says.
 */
export const DEFERRED_ROW_FOLLOW_UP_INTERVAL_MS = 15_000;
export const DEFERRED_ROW_FOLLOW_UP_LIFETIME_MS = 15 * 60_000;
export const DEFERRED_ROW_FOLLOW_UP_MAX_REATTEMPTS = 3;

export type DeferredRowFollowUpEvent =
	| "complete"
	| "complete-by-registry"
	| "cancelled"
	| "reattempt"
	| "reattempt-failed"
	| "row-missing-unverified"
	| "read-failed"
	| "no-longer-deferred"
	| "gave-up";

export interface DeferredRowWatch {
	sessionId: string;
	fileName?: string;
	/** Absolute instant after which the watch gives up; the caller keeps it across repeated yields. */
	deadlineAt: number;
	/** The entry is still rendered as deferred/handed off and not cancelled, failed, or finished locally. */
	isStillDeferred(): boolean;
	/** May the lane take this row back? Identity must still match, budget must remain, target must be live. */
	canReattempt(row: FileUploadSession): boolean;
	isRetryDue(row: Pick<FileUploadSession, "id" | "retryAt" | "retryAttempt">, now: number): boolean;
	onComplete(): void;
	onCancelled(): void;
	/** Re-run the upload for this entry. Awaited; this watch is over once it settles either way. */
	onReattempt(row: FileUploadSession): Promise<void>;
	/**
	 * The row is gone. Answer "complete" only on authoritative proof (the registry says assembled);
	 * `"absent"` is the registry positively answering that no upload with this identity exists — the
	 * one verdict that PROVES nothing will ever finish this row. Anything else is doubt, and doubt is
	 * reported as such so the copy the user sees can be honest about it.
	 */
	onRowMissing(): Promise<"complete" | "absent" | "unknown">;
	/**
	 * LSP-19767 (data-loss): the durable row is gone and `onRowMissing` could NOT prove the file was
	 * assembled, so nothing is going to finish this upload and nothing is going to retry it. Before
	 * this hook the watch just ended here, leaving the tray rendering "Finishing in the background…"
	 * for the life of the page — the browser evicts this storage on its own (low disk, Safari's
	 * 7-day cap, private windows, "clear site data"), so the customer did nothing wrong and was
	 * told nothing at all. Optional so an out-of-tree watch keeps compiling; the dialog wires it.
	 *
	 * `evidence` is what the probe could actually prove. `"absent"` means the registry positively
	 * answered that no such upload exists; `"unverified"` means the probe gave no answer worth
	 * trusting — it threw, it was unreadable, or it reported a status that is simply not assembled
	 * yet. The distinction is load-bearing for the COPY: telling someone their file is gone when the
	 * server may already hold it invites a duplicate upload, which is the opposite of the honesty
	 * this state exists to provide.
	 */
	onLocalRecordLost?(evidence: "absent" | "unverified"): void;
	/** Silent-failure rule: every exit of a watch names itself here. */
	log(event: DeferredRowFollowUpEvent, details: Record<string, unknown>): void;
}

export interface DeferredRowFollowUpRegistryOptions {
	readRows(sessionIds: readonly string[]): Promise<ReadonlyMap<string, FileUploadSession | undefined>>;
	intervalMs?: number;
	now?: () => number;
}

export interface DeferredRowFollowUpRegistry {
	/** Start (or replace) the watch for a session. */
	watch(watch: DeferredRowWatch): void;
	stop(sessionId: string): void;
	stopAll(): void;
	has(sessionId: string): boolean;
	/** Resolves once the tick in flight, if any, has settled. Test seam. */
	settle(): Promise<void>;
}

export function createDeferredRowFollowUpRegistry(options: DeferredRowFollowUpRegistryOptions): DeferredRowFollowUpRegistry {
	const intervalMs = options.intervalMs ?? DEFERRED_ROW_FOLLOW_UP_INTERVAL_MS;
	const now = options.now ?? (() => Date.now());
	const watches = new Map<string, DeferredRowWatch>();
	let timer: ReturnType<typeof globalThis.setTimeout> | undefined;
	let tickInFlight: Promise<void> | undefined;

	const log = (watch: DeferredRowWatch, event: DeferredRowFollowUpEvent, details: Record<string, unknown> = {}) => {
		try {
			watch.log(event, { sessionId: watch.sessionId, fileName: watch.fileName, ...details });
		} catch {
			// A diagnostic must never change an upload's outcome.
		}
	};
	const schedule = () => {
		if (timer !== undefined || watches.size === 0) return;
		timer = globalThis.setTimeout(() => {
			timer = undefined;
			tickInFlight = tick().finally(() => {
				tickInFlight = undefined;
				schedule();
			});
		}, intervalMs);
	};
	/**
	 * LSP-19767: both `row-missing-unverified` exits, and only those. A verified-complete row goes
	 * through `onComplete`, and a row that is still THERE is somebody's to finish — this is the one
	 * state where the local record has vanished with the file unaccounted for.
	 */
	const surfaceLocalRecordLost = (watch: DeferredRowWatch, evidence: "absent" | "unverified") => {
		try {
			watch.onLocalRecordLost?.(evidence);
		} catch (error) {
			// Surfacing the loss must never turn one lost row into a broken tick for its siblings — but
			// swallowing it without a word would re-create the very silence this hook exists to end:
			// the watch is finished immediately after, so nothing comes back, and the `row-missing-
			// unverified` line above would make the exit look handled. CLAUDE.md "Diagnostic logging
			// when fixing silent failures" applies to this catch as much as to the conditions it guards
			// (AI review, PR #4964).
			try {
				console.warn("[Upload] Could not surface a lost local upload record; the tray row was left as it was", {
					sessionId: watch.sessionId,
					evidence,
					error
				});
			} catch {
				// A diagnostic failure must not turn a swallowed surfacing error into a thrown tick.
			}
		}
	};
	/**
	 * Still this session's registered watch AND still a row the tray is presenting as unfinished.
	 *
	 * Both halves are needed after an await, and for different reasons. The identity half catches a
	 * watch that was stopped or replaced; the `isStillDeferred` half catches the case identity CANNOT
	 * see — a completion landing during the probe, because completing a row does not stop its watch
	 * (`markResumedFileComplete` clears the row's flags and leaves the registry alone).
	 */
	const readWatchLiveness = (watch: DeferredRowWatch): "live" | "superseded" | "no-longer-deferred" | "predicate-threw" => {
		if (watches.get(watch.sessionId) !== watch) return "superseded";
		try {
			return watch.isStillDeferred() ? "live" : "no-longer-deferred";
		} catch (error) {
			// An unreadable predicate is not permission to mutate the row — but it must not be reported
			// as a SUPERSEDED watch either. This catch used to be the one silent catch in the change, and
			// its callers then logged `evidence: "stale-watch"`, asserting a specific and FALSE
			// explanation for what was really an unhandled exception (AI review, PR #4964, critical).
			try {
				console.warn("[Upload] Could not read whether a deferred row is still deferred; declining to surface a loss", {
					sessionId: watch.sessionId,
					error
				});
			} catch {
				// A diagnostic failure must not turn a declined check into a thrown tick.
			}
			return "predicate-threw";
		}
	};
	const finishWatch = (watch: DeferredRowWatch) => {
		// Only remove the watch that is still registered for this id: a re-attempt that yields again
		// registers a replacement before this tick returns, and that replacement must survive.
		if (watches.get(watch.sessionId) === watch) watches.delete(watch.sessionId);
	};

	const settleOne = async (watch: DeferredRowWatch, row: FileUploadSession | undefined): Promise<void> => {
		if (watches.get(watch.sessionId) !== watch) return; // stopped or replaced while the read was in flight
		if (!watch.isStillDeferred()) {
			log(watch, "no-longer-deferred");
			return finishWatch(watch);
		}
		if (!row) {
			let verdict: "complete" | "absent" | "unknown" = "unknown";
			let livenessAfterProbe: ReturnType<typeof readWatchLiveness> = "live";
			try {
				verdict = await watch.onRowMissing();
			} catch (error) {
				// Same staleness re-check the success path below has always had, and now load-bearing on
				// this path too: before LSP-19767 the catch only LOGGED, so a watch stopped or replaced
				// during the probe cost nothing. It now performs a visible mutation, and applying it to a
				// row this watch no longer owns would park a cancelled or superseded upload (AI review,
				// PR #4964). The probe is a network call, so the window is real.
				const livenessAfterThrow = readWatchLiveness(watch);
				if (livenessAfterThrow !== "live") {
					log(watch, "row-missing-unverified", { probe: "threw", evidence: livenessAfterThrow, error });
					return finishWatch(watch);
				}
				log(watch, "row-missing-unverified", { probe: "threw", evidence: "unverified", error });
				surfaceLocalRecordLost(watch, "unverified");
				return finishWatch(watch);
			}
			if (watches.get(watch.sessionId) !== watch) return;
			if (verdict === "complete") {
				log(watch, "complete-by-registry");
				watch.onComplete();
			} else if ((livenessAfterProbe = readWatchLiveness(watch)) !== "live") {
				// The row finished (or stopped being deferred) WHILE the probe was in flight. The identity
				// check above cannot see that: completing a row does not stop or replace its watch, so the
				// registered watch is still this one. Surfacing here would re-park an upload that just
				// succeeded and tell its owner to send the file again — the mirror image of the
				// loss-then-complete ordering `markResumedFileComplete` already handles, and the one that
				// invites a duplicate of a file the server is safely holding.
				log(watch, "row-missing-unverified", { probe: verdict, evidence: livenessAfterProbe });
			} else {
				// `absent` is the registry's positive "no such upload"; `unknown` is doubt. Both end the
				// watch and both surface, but only the first may be described to the user as a fact.
				const evidence = verdict === "absent" ? "absent" : "unverified";
				log(watch, "row-missing-unverified", { probe: verdict, evidence });
				surfaceLocalRecordLost(watch, evidence);
			}
			return finishWatch(watch);
		}
		if (row.status === "complete") {
			log(watch, "complete");
			watch.onComplete();
			return finishWatch(watch);
		}
		if (row.status === "cancelled") {
			log(watch, "cancelled");
			watch.onCancelled();
			return finishWatch(watch);
		}
		const ownerless = row.uploadOwnerId === undefined;
		const current = now();
		// The deadline gates re-admission too (Codex diff review): a row that keeps coming back
		// ownerless after the deadline is the drain's, not this lane's — otherwise repeated yields could
		// run the full re-attempt budget long after the tray promised a bound.
		if (current >= watch.deadlineAt) {
			log(watch, "gave-up", {
				status: row.status,
				ownerHeldElsewhere: !ownerless,
				retryAt: row.retryAt ?? null,
				retryAttempt: row.retryAttempt,
				reattemptAllowed: watch.canReattempt(row)
			});
			return finishWatch(watch);
		}
		if (row.status === "pending" && ownerless && watch.isRetryDue(row, current) && watch.canReattempt(row)) {
			log(watch, "reattempt", { retryAttempt: row.retryAttempt, lastError: row.lastError });
			// The watch is over the moment the lane takes the row back; a fresh yield registers a fresh
			// watch (against the same caller-held deadline). The attempt itself is dispatched OFF the tick:
			// it holds an upload slot for as long as the upload takes, and awaiting it here would stop every
			// sibling watch from seeing its own completion, cancellation, or deadline meanwhile.
			finishWatch(watch);
			void Promise.resolve()
				.then(() => watch.onReattempt(row))
				.catch((error) => log(watch, "reattempt-failed", { error }));
		}
	};

	const tick = async (): Promise<void> => {
		const active = [...watches.values()];
		if (active.length === 0) return;
		let rows: ReadonlyMap<string, FileUploadSession | undefined>;
		try {
			rows = await options.readRows(active.map((watch) => watch.sessionId));
		} catch (error) {
			// One unreadable tick is not a verdict about any row; each watch still ends at its own deadline.
			const current = now();
			for (const watch of active) {
				if (watches.get(watch.sessionId) !== watch) continue;
				log(watch, "read-failed", { error });
				if (current >= watch.deadlineAt) {
					log(watch, "gave-up", { reason: "read-failed" });
					// LSP-19767 (AI review, PR #4964): an eviction that makes the durable read THROW rather
					// than answer empty used to end here with nothing said — reproducing this ticket's own
					// symptom through a different door. Giving up on a row we could never read is still a
					// row nothing is going to finish, so it is surfaced as doubt, never as fact.
					if (readWatchLiveness(watch) === "live") surfaceLocalRecordLost(watch, "unverified");
					finishWatch(watch);
				}
			}
			return;
		}
		await Promise.all(active.map((watch) => settleOne(watch, rows.get(watch.sessionId))));
	};

	return {
		watch(watch) {
			watches.set(watch.sessionId, watch);
			schedule();
		},
		stop(sessionId) {
			watches.delete(sessionId);
			if (watches.size === 0 && timer !== undefined) {
				globalThis.clearTimeout(timer);
				timer = undefined;
			}
		},
		stopAll() {
			watches.clear();
			if (timer !== undefined) globalThis.clearTimeout(timer);
			timer = undefined;
		},
		has: (sessionId) => watches.has(sessionId),
		settle: async () => {
			await tickInFlight;
		}
	};
}
